import { useCallback, useEffect, useRef, useState } from 'react';
import { useDeviceDetect } from 'react-easyrocket';
import { useNavigate } from 'react-router';
import { useModalContext } from 'src/hooks/useModalContext';
import { useRoute } from 'src/router/hooks/useRoute';

type BeforeInstallPromptEvent = Event & {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
};

export type PwaPromptTools = {
  isPwaPopupVisible: boolean;
  showPwaPopup: () => void;
  hidePwaPopup: () => void;
  promptPwaPopup: () => void;
};

export const useInstallPwaPrompt = (): PwaPromptTools => {
  const { isIos } = useDeviceDetect();
  const navigate = useNavigate();
  const { openModal } = useModalContext();
  const { ROUTE } = useRoute();
  const [isPwaPopupVisible, setPwaPopupVisible] = useState(false);

  const promptEvent = useRef<BeforeInstallPromptEvent | null>(null);
  const isPwaInstalled = useRef<boolean>(false);

  useEffect(() => {
    const handleBeforeInstallPrompt: EventListenerOrEventListenerObject = (e) => {
      e.preventDefault();
      promptEvent.current = e as BeforeInstallPromptEvent;
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    const onInstall = () => {
      isPwaInstalled.current = true;
    };
    window.addEventListener('appinstalled', onInstall);
    return () => {
      window.removeEventListener('appinstalled', onInstall);
    };
  }, []);

  const promptPwaPopup = useCallback(() => {
    if (isIos) {
      openModal('IOS_SETUP');
      return;
    }

    !isPwaInstalled.current && promptEvent.current?.prompt();
    navigate(ROUTE.PWA_INSTRUCTION.get(''));
  }, [isIos, openModal, navigate]);

  return {
    promptPwaPopup,
    isPwaPopupVisible,
    showPwaPopup: () => setPwaPopupVisible(true),
    hidePwaPopup: () => setPwaPopupVisible(false)
  };
};
